// =============================================================================
// VandieZ agent-web -- Trips list (/trips)
// =============================================================================
// Lists all trips for the currently-selected owner, sorted by booked_at DESC.
// Each row shows: status pill, pickup coords → drop coords, driver phone
// (pickup_contact_phone if revealed), trip_value, booked_at.
//
// Each row links to /trips/:tripId?as=<currentOwnerId>.
//
// v1: read-only. Status updates and PoD actions are deferred to Phase 2.
// =============================================================================

import { Loader2, Route } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { tripsApi } from "@/api/client";
import { AppHeader } from "@/components/AppHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useOwnerContext } from "@/lib/owner-context";
import { ApiError } from "@/types/auth";
import type { Trip, TripStatus } from "@/types/trip";

const STATUS_LABEL: Record<TripStatus, string> = {
  booked: "Booked",
  en_route_to_pickup: "En route",
  arrived_at_pickup: "At pickup",
  loading: "Loading",
  in_transit: "In transit",
  arrived_at_drop: "At drop",
  unloading: "Unloading",
  delivered: "Delivered",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_TONE: Record<TripStatus, string> = {
  booked: "bg-muted text-muted-foreground",
  en_route_to_pickup: "bg-primary-subtle text-primary-subtle-foreground",
  arrived_at_pickup: "bg-primary-subtle text-primary-subtle-foreground",
  loading: "bg-warning-subtle text-warning-subtle-foreground",
  in_transit: "bg-success-subtle text-success-subtle-foreground",
  arrived_at_drop: "bg-success-subtle text-success-subtle-foreground",
  unloading: "bg-warning-subtle text-warning-subtle-foreground",
  delivered: "bg-primary-subtle text-primary-subtle-foreground",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-danger-subtle text-danger-subtle-foreground",
};

function formatINR(amount: string): string {
  const n = Number.parseFloat(amount);
  if (Number.isNaN(n)) {return amount;}
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function shortCoords(lat: string, lng: string): string {
  const la = Number.parseFloat(lat);
  const lo = Number.parseFloat(lng);
  if (Number.isNaN(la) || Number.isNaN(lo)) {return `${lat}, ${lng}`;}
  return `${la.toFixed(3)}, ${lo.toFixed(3)}`;
}

export function TripsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const { currentOwnerId, currentOwner } = useOwnerContext();
  const [trips, setTrips] = useState<Trip[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (accessToken === null || currentOwnerId === null) {
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    tripsApi
      .list(accessToken, currentOwnerId, { limit: 50 }, controller.signal)
      .then((res) => {
        // Sort by booked_at DESC
        const sorted = [...res.items].sort(
          (a, b) => new Date(b.booked_at).getTime() - new Date(a.booked_at).getTime(),
        );
        setTrips(sorted);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        if (err instanceof ApiError) {
          const code = err.problem.code;
          if (err.problem.status === 403 && code === "AGENT_PERMISSION_MISSING") {
            setError("Ask the owner to grant the 'read' permission on 'trips'.");
          } else if (err.problem.status === 403 && code === "AGENT_NO_GRANT_FOR_OWNER") {
            setError("Your access to this owner was revoked. Switch owner via the header dropdown.");
          } else if (err.problem.status === 422 && code === "AGENT_OWNER_PARAM_REQUIRED") {
            setError("No owner selected — switch via the header dropdown.");
          } else {
            setError(err.problem.detail);
          }
        } else {
          setError("Couldn't load trips.");
        }
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, currentOwnerId]);

  const ownerName = currentOwner?.owner_full_name ?? "This owner";

  if (currentOwnerId === null) {
    return (
      <div className="min-h-screen bg-muted/30">
        <AppHeader title="Trips" />
        <main className="mx-auto max-w-4xl px-6 py-16 text-center text-muted-foreground">
          No owner selected — head to{" "}
          <Link to="/" className="font-medium text-primary underline underline-offset-2">
            My Owners
          </Link>{" "}
          and pick one.
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 pb-16">
      <AppHeader title="Trips" />

      <main className="mx-auto max-w-4xl space-y-6 px-6 py-8">
        {error !== null ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : trips === null || trips.length === 0 ? (
          <Card>
            <CardHeader className="items-center text-center">
              <Route className="h-10 w-10 text-muted-foreground" />
              <CardTitle>No trips yet</CardTitle>
              <CardDescription>
                {ownerName} doesn&apos;t have any trips yet. Trips appear after
                a bid is accepted by a shipper.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="space-y-3">
            {trips.map((trip) => (
              <Link
                key={trip.id}
                to={`/trips/${trip.id}?as=${currentOwnerId}`}
                className="block"
              >
                <Card className="group cursor-pointer transition-colors hover:border-primary/50">
                  <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-4">
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_TONE[trip.status]}`}
                        >
                          {STATUS_LABEL[trip.status]}
                        </span>
                      </div>
                      <p className="text-sm font-medium">
                        {shortCoords(trip.pickup_lat, trip.pickup_lng)}
                        {" → "}
                        {shortCoords(trip.drop_lat, trip.drop_lng)}
                      </p>
                      {trip.pickup_contact_phone !== null ? (
                        <p className="text-xs text-muted-foreground">
                          Driver: {trip.pickup_contact_phone}
                        </p>
                      ) : null}
                    </div>
                    <div className="text-right text-xs text-muted-foreground space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        {formatINR(trip.trip_value)}
                      </p>
                      <p>{formatDate(trip.booked_at)}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
